'use client';

import { ReactNode, createContext, useCallback, useEffect, useMemo, useState } from 'react';

import { WidgetStylesConfiguration } from '@vestico/model';

import { VideoPlaybackContextProvider } from './components/VideoPlaybackContextProvider';
import { StylesConfig } from './services/stylesConfiguration';

export interface FormValidationProps {
  updateStep: (step: string, valid: boolean) => void;
  submit: () => void;
  submitStart: () => void;
  submitEnd: () => void;
}

export interface Globals {
  useMobileLayout: boolean;
  isTouchDevice: boolean;
  isWindows: boolean;
  stylesConfig: StylesConfig;
}

export const FormValidationContext = createContext<FormValidationProps>({} as FormValidationProps);
export const GlobalsContext = createContext<Globals>({} as Globals);

interface CommonAppContextProps {
  styles?: WidgetStylesConfiguration;
  children: ReactNode;
}

function calcUseMobileLayout(): boolean {
  const mobileLayoutBreakpoint = 600;
  return document.documentElement.clientWidth <= mobileLayoutBreakpoint;
}

export const CommonAppContext = ({ styles, children }: CommonAppContextProps) => {
  const [useMobileLayout, setUseMobileLayout] = useState(false);
  const isTouchDevice = useMemo(() => typeof window !== 'undefined' && ('ontouchstart' in window || navigator.maxTouchPoints > 0), []);

  useEffect(() => {
    setUseMobileLayout(calcUseMobileLayout());
  }, []);

  const handleWindowResize = useCallback(() => {
    setUseMobileLayout(calcUseMobileLayout());
  }, []);

  const isWindows = useMemo(() => typeof window !== 'undefined' && navigator.appVersion.includes('Win'), []);

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [handleWindowResize]);

  const stylesConfig = useMemo(() => {
    return new StylesConfig(styles);
  }, [styles]);

  const globalsContextValue = useMemo(() => {
    return { useMobileLayout, isTouchDevice, isWindows, stylesConfig };
  }, [useMobileLayout, isTouchDevice, isWindows, stylesConfig]);

  return (
    <GlobalsContext.Provider value={globalsContextValue}>
      <VideoPlaybackContextProvider>{children}</VideoPlaybackContextProvider>
    </GlobalsContext.Provider>
  );
};

CommonAppContext.defaultProps = { styles: {} };
